import { g100, white } from '@carbon/themes';
// A tipagem publicada do @carbon/colors aponta para um arquivo que não vem no pacote (o
// `js/generated`), e o TypeScript não enxerga as cores. O JavaScript está certo: o módulo inteiro
// entra e cada cor sai pelo nome.
import * as coresDoCarbon from '@carbon/colors';

// ─── AS CORES DO TEMA PARA QUEM NÃO LÊ `var(--cds-*)` ───────────────────────────────────────
//
// Estilo e folha usam `var(--cds-*)`, que troca sozinho entre o claro e o escuro. Mas atributo de
// SVG (`fill=`, `stroke=`), canvas, o wavesurfer e o exportar-imagem precisam do VALOR, e não da
// variável. Aqui eles o pegam do `@carbon/themes`, no tema que está no ar (a classe que o
// TemaProvider põe no <html>). Nenhuma cor é escrita à mão.

export type TokenDoTema = keyof typeof white;

const temaAtual = () =>
  (typeof document !== 'undefined' && document.documentElement.classList.contains('cds--g100') ? g100 : white);

/** O valor de um token do Carbon no tema que está no ar (ex.: `corDoTema('textHelper')`). */
export const corDoTema = (token: TokenDoTema): string => String(temaAtual()[token]);

/** O valor de um token num tema FIXO, para o que não acompanha a tela (a miniatura de um papel escuro). */
export const corDoTemaFixo = (tema: 'white' | 'g100', token: TokenDoTema): string => String((tema === 'g100' ? g100 : white)[token]);

/**
 * Os nomes do `COR` do núcleo, apontando para o token do Carbon pelo papel. É o que a web usava
 * do `COR` (que agora é só do app nativo).
 */
export const PAPEL_DO_COR = {
  primaria: 'interactive',
  primariaEscura: 'linkPrimaryHover',
  sobrePrimaria: 'textOnColor',
  titulo: 'textPrimary',
  texto: 'textPrimary',
  secundario: 'textSecondary',
  apagado: 'textHelper',
  espaçoReservado: 'textPlaceholder',
  superficie: 'layer01',
  fundo: 'background',
  contorno: 'borderSubtle01',
  divisoria: 'borderSubtle00',
  destaque: 'layerSelected01',
  marca: 'interactive',
  erro: 'supportError',
  alerta: 'supportWarning',
} as const satisfies Record<string, TokenDoTema>;

const emKebab = (t: string) => t.replace(/([a-z])([A-Z0-9])/g, '$1-$2').replace(/([0-9])([a-z])/g, '$1-$2').toLowerCase();

/** O mesmo papel como `var(--cds-*)`, para estilo em linha. */
export const varDoPapel = (papel: keyof typeof PAPEL_DO_COR) => `var(--cds-${emKebab(PAPEL_DO_COR[papel])})`;

// A paleta CATEGÓRICA de dados do Carbon (a ordem da documentação de data-vis), no claro e no
// escuro: as séries de um gráfico, a mesma ordem em qualquer gráfico do produto.
const cor = (nome: string) => (coresDoCarbon as unknown as Record<string, string>)[nome];
const CATEGORICAS_CLARO = ['purple70', 'cyan50', 'teal70', 'magenta70', 'red50', 'red90', 'green60', 'blue80', 'magenta50', 'yellow50', 'teal50', 'cyan90', 'orange70', 'purple50'].map(cor);
const CATEGORICAS_ESCURO = ['purple60', 'cyan40', 'teal60', 'magenta40', 'red50', 'red10', 'green30', 'blue50', 'magenta60', 'yellow40', 'teal40', 'cyan20', 'orange60', 'purple30'].map(cor);

/** A cor da série N de um gráfico, no tema que está no ar. Dá a volta depois da 14ª. */
export const corDaSerie = (indice: number): string => {
  const paleta = temaAtual() === g100 ? CATEGORICAS_ESCURO : CATEGORICAS_CLARO;
  return paleta[((indice % paleta.length) + paleta.length) % paleta.length];
};
